import { ConditionalRender } from '@ui';
import { useMemo } from 'react';
import { groupBy } from 'lodash-es';
import { logisticsManagementGroups } from '@/constants/user.constants';
import { ResourceSection } from '../ResourceSection';
import { NoContacts } from '../NoContacts';
import { AssignedUsersByGroup, CaseResourceType, CaseUserGroup, EmployeeLabels } from '@/api/apis/case';
import { EmployeeCard } from '../EmployeeCard';
import { getRoutePilots } from '../../utils';
import { AircraftLogisticsResourceType, RouteTypeEnum } from '@/api/apis/route';
import { RouteResources } from './RouteResources';
import { Uuid } from '@/api/types';
import { sortUsersByGroup } from '@/utils/helpers';
import { LOGISTICS_CASE_MANAGEMENT_GROUP_WEIGHT } from '@/constants/case/case.constants';
import { PrimaryWarningBanner } from '../PrimaryWarningBanner';
import { useLogisticsById } from '@/queries/route-planner';
import { useLogisticsDrivers } from '../../hooks/useLogisticsDrivers';

const primaryGroups = [
  CaseUserGroup.LogisticsManagers,
  CaseUserGroup.AviationSpecialists,
  CaseUserGroup.GroundSpecialists,
];

type CaseManagementProps = {
  assignedEmployees: AssignedUsersByGroup;
  menuDisabled: boolean;
  logisticsId?: string;
  caseId: Uuid;
};

export const LogisticsManagement = ({ assignedEmployees, menuDisabled, logisticsId, caseId }: CaseManagementProps) => {
  const { data: logisticsProjection } = useLogisticsById(logisticsId);
  const mainRouteData = useMemo(
    () => logisticsProjection?.routes.find((r) => r.routeType === RouteTypeEnum.Main),
    [logisticsProjection],
  );
  const resourceGroups = groupBy(logisticsProjection?.resources || [], 'resourceType');

  const users = useMemo(() => {
    const list = logisticsManagementGroups.flatMap((group) => {
      return assignedEmployees[group as CaseUserGroup] ?? [];
    });

    return sortUsersByGroup(list, LOGISTICS_CASE_MANAGEMENT_GROUP_WEIGHT, true);
  }, [assignedEmployees]);

  const aircrafts = (resourceGroups[CaseResourceType.Aircraft] as AircraftLogisticsResourceType[]) ?? [];
  const pilots = useMemo(
    () => getRoutePilots(logisticsProjection?.resources, mainRouteData),
    [logisticsProjection?.resources, mainRouteData],
  );
  const { drivers } = useLogisticsDrivers(logisticsProjection);
  const cardsLength = users.length + aircrafts.length + pilots.length + drivers.length;

  const primaryAvailableUsers = useMemo(() => {
    let availableUsers = {};
    for (const [key, value] of Object.entries(assignedEmployees)) {
      const currentGroup = assignedEmployees[key as CaseUserGroup];
      if (currentGroup && primaryGroups.includes(key as CaseUserGroup) && currentGroup.length > 0) {
        availableUsers = {
          ...availableUsers,
          [key]: value,
        };
      }
    }

    return availableUsers;
  }, [assignedEmployees]);

  return (
    <ResourceSection title="Logistics Case Management" count={cardsLength}>
      <PrimaryWarningBanner userGroups={primaryGroups} assignedEmployees={assignedEmployees} />
      <ConditionalRender show={!!cardsLength} placeholder={<NoContacts />}>
        {users.map(({ user, userId, employeeId, group, isPrimary }) => (
          <EmployeeCard
            menuDisabled={menuDisabled}
            key={`${userId || employeeId}_${group}`}
            employeeId={employeeId!}
            user={user}
            group={group}
            primaryAvailableUsers={primaryAvailableUsers}
            labels={isPrimary ? [EmployeeLabels.Primary] : []}
            caseId={caseId}
            assignedUsersInGroup={assignedEmployees[group as CaseUserGroup] || []}
          />
        ))}
        <RouteResources aircrafts={aircrafts} drivers={drivers} pilots={pilots} />
      </ConditionalRender>
    </ResourceSection>
  );
};
